import { Injectable, inject } from '@angular/core';
import { EventProperties } from '@datarails/analytics';
import { AnalyticsService } from '@datarails/analytics/angular';

import { StorageKey } from '@core/storage-keys.enum';
import { environment } from '../../../environments/environment';
import { buildPlgAnalyticsConfig } from './analytics-bootstrap.factory';

/**
 * Mixpanel persists its state under `mp_<token>_mixpanel`. We match on the `mp_`
 * prefix AND the `_mixpanel` marker (not the prefix alone) so we only clear this
 * app's Mixpanel entries, never an unrelated `mp_`-prefixed key. We clear
 * persistence directly — rather than a public SDK reset — because at init no
 * in-session `identify()` has run, so the SDK's `setUser(undefined)`/`onLogout`
 * path no-ops while the stale `distinct_id` sits in persistence. If Mixpanel ever
 * changes these key names, clearing simply no-ops (prior behavior), never throws.
 */
const isMixpanelStorageKey = (key: string): boolean => key.startsWith('mp_') && key.includes('_mixpanel');

/**
 * Thin app-wide wrapper over the shared `@datarails/analytics` Angular service.
 * Registered as an APP_INITIALIZER; `init()` resolves before components render,
 * so callers never race the underlying client.
 */
@Injectable({ providedIn: 'root' })
export class AnalyticsSdkService {
    private readonly analytics = inject(AnalyticsService);
    private initPromise?: Promise<void>;

    /**
     * True when a Mixpanel token is configured. Consumers gate their own
     * side effects (e.g. localStorage lifecycle) on this so the "disabled =
     * silent no-op" contract holds — with no token, no provider is registered,
     * so track/identify are already no-ops, but callers should skip side effects too.
     */
    isEnabled(): boolean {
        return !!environment.mixpanelToken;
    }

    init(): Promise<void> {
        if (this.initPromise) return this.initPromise;
        // Consume a pending identity-reset BEFORE the SDK initializes, so a new
        // PLG contact on this browser starts with a fresh Mixpanel distinct_id
        // instead of inheriting the previous contact's persisted identity.
        this.consumePendingIdentityReset();
        const config = buildPlgAnalyticsConfig({
            mixpanelToken: environment.mixpanelToken,
            orgId: environment.analyticsOrgId,
            production: environment.production,
        });
        this.initPromise = this.analytics.init(config).catch(err => {
            // Analytics must never break the app — swallow and log.
            console.error('[AnalyticsSdkService] init failed', err);
        });
        return this.initPromise;
    }

    track(eventName: string, properties: EventProperties = {}): void {
        if (!eventName) return;
        this.analytics.track(eventName, properties);
    }

    /**
     * Identifies the user by the non-PII PLG session id. An optional `profile`
     * (email/name) is written to the Mixpanel USER PROFILE via `people.set` — the
     * `@datarails/analytics` client maps it to `$email`/`$first_name`/`$last_name`
     * and never places it on events or super-properties.
     */
    identify(sessionId: string, profile?: { email?: string; firstName?: string; lastName?: string }): void {
        if (!sessionId) return;
        this.analytics.identify({ id: sessionId, ...(profile ?? {}) });
    }

    /**
     * If a PLG reset flagged the identity as stale, drop Mixpanel's persisted
     * `localStorage` so `analytics.init()` below starts a fresh anonymous
     * distinct_id. This must run before init: the AppModule composes the
     * initializers so `PlgTokenService.init()` (which sets the flag) is awaited
     * before `AnalyticsSdkService.init()`, so the flag is set by the time this
     * consumes it — an explicit sequence, not a reliance on registration order.
     */
    private consumePendingIdentityReset(): void {
        if (localStorage.getItem(StorageKey.AnalyticsIdentityReset) !== '1') return;
        localStorage.removeItem(StorageKey.AnalyticsIdentityReset);
        // Iterate backwards: removeItem shifts indices.
        for (let i = localStorage.length - 1; i >= 0; i--) {
            const key = localStorage.key(i);
            if (key && isMixpanelStorageKey(key)) {
                localStorage.removeItem(key);
            }
        }
    }
}
